import { AskReadooBox } from "@/components/AskReadooBox";
import { useAds, AdRender, matchTarget, consentOk } from "@/components/Ads";

function renderInline(text, keyPrefix) {
  // Bold **...** support
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={`${keyPrefix}-b-${i}`} className="font-semibold text-onSurface">{p.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-t-${i}`}>{p}</span>;
  });
}

function parseBlocks(content = "") {
  // elenchi numerati salvati su una sola riga ("1. … 2. … 3. …") → una voce per riga
  const lines = content.replace(/\r/g, "").split("\n")
    .flatMap((l) => (/^\s*1[.)]\s/.test(l) && /\s2[.)]\s/.test(l) ? l.split(/\s+(?=\d{1,2}[.)]\s)/) : [l]));
  const blocks = [];
  let buffer = [];
  const flush = () => {
    if (buffer.length) {
      const txt = buffer.join(" ").trim();
      if (txt) blocks.push({ type: "p", text: txt });
      buffer = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }
    const h = line.match(/^(#{2,4})\s+(.*)$/);
    if (h) { flush(); blocks.push({ type: "h2", text: h[2].trim() }); continue; }
    const li = line.match(/^\d+[.)]\s+(.*)$/);
    if (li) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last?.type === "ol") last.items.push(li[1]);
      else blocks.push({ type: "ol", items: [li[1]] });
      continue;
    }
    buffer.push(line);
  }
  flush();
  // Drop orphan headings: keep an H2 only if it is immediately followed by a paragraph.
  return blocks.filter((b, i) => b.type !== "h2" || ["p", "ol"].includes(blocks[i + 1]?.type));
}

// Voce di elenco: "♌ **Leone** ★★★★★ — frase" → nome e stelle sopra, frase sotto
function ListItem({ text, n, k }) {
  const [head, ...rest] = text.split(" — ");
  const tail = rest.join(" — ");
  return (
    <li data-testid={`article-list-item-${n}`} className="flex gap-3 py-3 border-b border-hair last:border-b-0">
      <span className="font-mono text-sm font-bold text-brand w-6 shrink-0 pt-1 tabular-nums">{n}.</span>
      <div className="min-w-0">
        <p className="text-lg leading-snug text-onSurface">{renderInline(head, `${k}h`)}</p>
        {tail && <p className="mt-0.5 text-base leading-relaxed text-onSurfaceSecondary">{renderInline(tail, `${k}t`)}</p>}
      </div>
    </li>
  );
}

export function ArticleBody({ content, articleId, showAsk = true, category, promo = null }) {
  const blocks = parseBlocks(content);
  const paragraphIdx = blocks.map((b, i) => (b.type === "p" ? i : -1)).filter((i) => i >= 0);
  const askAfter = paragraphIdx.length ? paragraphIdx[Math.floor(paragraphIdx.length / 2)] : -1;

  const { blocks: adBlocks, consentGranted } = useAds();
  const ctx = { page: "article", category };
  const paraAds = (num, placement) =>
    (adBlocks || []).filter(
      (b) => b.placement === placement && (b.paragraph || 1) === num && matchTarget(b, ctx) && consentOk(b, consentGranted)
    );

  let paragraphNo = 0;
  const firstH2 = blocks.findIndex((b) => b.type === "h2");
  const promoAt = firstH2 > 0 ? firstH2 : Math.min(paragraphIdx[1] ?? paragraphIdx[0] ?? 0, blocks.length);

  return (
    <div data-testid="article-body" className="max-w-none">
      {blocks.map((b, i) => {
        const nodes = [];
        if (promo && i === promoAt) nodes.push(<div key="promo" className="my-8">{promo}</div>);
        if (b.type === "h2") {
          nodes.push(
            <h2 key={`h-${i}`} className="font-serif text-2xl sm:text-3xl font-semibold text-onSurface mt-10 mb-4 leading-snug scroll-mt-24">
              <span className="inline-block w-8 h-0.5 bg-brand mb-3 rounded-full" aria-hidden />
              <br />
              {b.text}
            </h2>
          );
        } else if (b.type === "ol") {
          nodes.push(
            <ol key={`ol-${i}`} data-testid="article-list" className="mb-8 rounded-2xl border border-hair bg-surfaceSecondary px-4 sm:px-5">
              {b.items.map((t, j) => <ListItem key={`li-${i}-${j}`} text={t} n={j + 1} k={`li${i}-${j}`} />)}
            </ol>
          );
        } else {
          paragraphNo += 1;
          const before = paraAds(paragraphNo, "before_paragraph");
          const after = paraAds(paragraphNo, "after_paragraph");
          before.forEach((ad) => nodes.push(<AdRender key={`bp-${ad.id}-${i}`} block={ad} />));
          nodes.push(
            <p key={`p-${i}`} className="text-lg leading-[1.8] text-onSurface/90 mb-6">
              {renderInline(b.text, `p${i}`)}
            </p>
          );
          after.forEach((ad) => nodes.push(<AdRender key={`ap-${ad.id}-${i}`} block={ad} />));
        }
        if (showAsk && i === askAfter) {
          nodes.push(<AskReadooBox key={`ask-${i}`} articleId={articleId} />);
        }
        return nodes;
      })}
    </div>
  );
}
